import { Typography, Grid, Card, Box, Skeleton} from '@mui/material'
import { Period, Scheme, NICBracket, RatesAndBrackets } from '../api-client';
import { useRatesAndBrackets } from '../hooks';

type Props = {
  scheme: Scheme | null
  period: Period | null
}

const CalculationsCard = (props: Props) => {
  const { data: ratesAndBrackets, isLoading, isPlaceholderData, error } = useRatesAndBrackets(
    props.scheme,
    props.period
  ) as {
    data?: RatesAndBrackets;
    isLoading: boolean;
    isPlaceholderData: boolean;
    error: Error | null;
  };

  if (error) {
    console.error('Error rendering calculations card:', error);
  }

  return (
    <Card sx={{ p: 4, maxWidth: '100%' }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
        How these figures are calculated
      </Typography>

      <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.6 }}>
        The additional costs of employing a member of staff are calculated by taking the
        base salary and adding the following components:
      </Typography>

      {!isLoading && !isPlaceholderData && !error && ratesAndBrackets ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* Employer Pension Section */}
          <Card variant="outlined" sx={{ p: 3, bgcolor: '#f8f9fa' }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: '#495057',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Box sx={{
                width: 8,
                height: 8,
                bgcolor: '#6c757d',
                borderRadius: '50%',
                mr: 1.5
              }} />
              Employer Pension Contributions
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
              Pension contributions are calculated based on the scheme selected.
              The employer pension contribution rate for the selected scheme and
              period is{' '}
              <Box component="span" sx={{ fontWeight: 'bold', color: '#495057' }}>
                {ratesAndBrackets.employer_pension_contribution_rate}
              </Box>.
            </Typography>
          </Card>

          {/* Employee Pension Section (Conditional) */}
          {ratesAndBrackets.employee_pension_contribution_rate && (
            <Card variant="outlined" sx={{ p: 3, bgcolor: '#f1f3f4' }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: '#5f6368',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Box sx={{
                  width: 8,
                  height: 8,
                  bgcolor: '#80868b',
                  borderRadius: '50%',
                  mr: 1.5
                }} />
                Employee Pension Contributions (Salary Exchange)
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                In this case, the employee's pension is deducted from their salary
                before tax and National Insurance contributions are calculated. The
                employee contribution rate for this scheme and period is{' '}
                <Box
                  component="span"
                  sx={{ fontWeight: 'bold', color: '#5f6368' }}
                >
                  {ratesAndBrackets.employee_pension_contribution_rate}
                </Box>.
              </Typography>
            </Card>
          )}

          {/* National Insurance Section */}
          <Card variant="outlined" sx={{ p: 3, bgcolor: '#f5f5f5' }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: '#424242',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Box sx={{
                width: 8,
                height: 8,
                bgcolor: '#757575',
                borderRadius: '50%',
                mr: 1.5
              }} />
              Employer National Insurance Contributions
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
              Employer National Insurance Contributions are calculated based on the
              salary, and are charged at different rates depending on the salary
              band in which the employee's salary falls.
            </Typography>
            {ratesAndBrackets.employee_pension_contribution_rate && (
              <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
                As described above, the employee's pension contributions are
                deducted from the salary to obtain the salary figure used for
                calculating National Insurance contributions.
              </Typography>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{ mb: 1.5, fontWeight: 600, color: '#424242' }}
              >
                Rate bands:
              </Typography>
              <Grid container spacing={1}>
                {ratesAndBrackets.national_insurance.brackets.map((
                  bracket: NICBracket, index: number
                ) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                    <Box sx={{
                      p: 2,
                      border: 1,
                      borderColor: '#e0e0e0',
                      borderRadius: 1,
                      bgcolor: 'white',
                      height: '100%'
                    }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, mb: 0.5 }}
                      >
                        {bracket.limit === 'Unlimited'
                          ? `Above ${
                              ratesAndBrackets.national_insurance
                                .brackets[index - 1]?.limit || '£0'
                            }`
                          : `Up to ${bracket.limit}`
                        }
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ color: '#757575', fontWeight: 'bold' }}
                      >
                        {bracket.rate}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Card>

          {/* Apprenticeship Levy Section */}
          <Card variant="outlined" sx={{ p: 3, bgcolor: '#fafafa' }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: '#616161',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Box sx={{
                width: 8,
                height: 8,
                bgcolor: '#9e9e9e',
                borderRadius: '50%',
                mr: 1.5
              }} />
              Apprenticeship Levy
            </Typography>
            {ratesAndBrackets.employee_pension_contribution_rate && (
              <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                As described above, the employee's pension contributions are deducted
                from the salary to obtain the salary figure used for calculating the
                Apprenticeship Levy.
              </Typography>
            )}
            <Typography variant="body1" sx={{ lineHeight: 1.6, mt: 2 }}>
              Apprenticeship Levy is charged at a flat rate of{' '}
              <Box component="span" sx={{ fontWeight: 'bold', color: '#616161' }}>
                {ratesAndBrackets.apprenticeship_levy_rate}
              </Box>{' '}
              on the base salary.
            </Typography>
          </Card>

        </Box>
      ) : (
        // Show skeletons if data is loading
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Skeleton /* pensions */
            variant="rectangular"
            height={147}
            data-testid="skeleton" />
          {ratesAndBrackets?.employee_pension_contribution_rate && (
            <Skeleton /* employee pensions */
              variant="rectangular"
              height={147}
              data-testid="skeleton"
            />
          )}
          <Skeleton /* national insurance */
            variant="rectangular"
            height={360}
            data-testid="skeleton"
          />
          <Skeleton /* apprenticeship levy */
            variant="rectangular"
            height={189}
            data-testid="skeleton"
          />
        </Box>
      )}
    </Card>
  )
}

export default CalculationsCard;
